import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  CloseCircleOutlined,
  CloseOutlined,
  HomeFilled,
  ReloadOutlined,
} from '@ant-design/icons';
import { Button, Menu, Tooltip } from 'antd';
import type { MenuProps } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { getNavigationItem } from '@/config/navigation';
import { useNavigationStore } from '@/stores/navigation-store';
import { queryClient } from '@/api/query-client';
import { preloadRouteModule } from '@/app/route-modules';

const HOME_PATH = '/dashboard';
const queryKeysByPath: Record<string, readonly (readonly unknown[])[]> = {
  '/dashboard': [['dashboard']],
  '/invoices': [['invoices']],
  '/numbering': [['numbering-tracks']],
  '/reports': [['report-daily']],
  '/exports': [['export-jobs']],
  '/exceptions': [['exceptions']],
  '/audit': [['audit-events']],
  '/iam': [['iam']],
};

export function WorkspaceTabs() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const scrollRef = useRef<HTMLDivElement>(null);
  const closingPathRef = useRef<string | null>(null);
  const navigationFrameRef = useRef<number | null>(null);
  const [pendingPath, setPendingPath] = useState<string | null>(null);
  const [contextMenu, setContextMenu] = useState<{ path: string; left: number; top: number } | null>(null);
  const openPaths = useNavigationStore((state) => state.openPaths);
  const openTab = useNavigationStore((state) => state.openTab);
  const refreshTab = useNavigationStore((state) => state.refreshTab);
  const closeTab = useNavigationStore((state) => state.closeTab);
  const closeLeft = useNavigationStore((state) => state.closeLeft);
  const closeRight = useNavigationStore((state) => state.closeRight);
  const closeOthers = useNavigationStore((state) => state.closeOthers);
  const closeAll = useNavigationStore((state) => state.closeAll);

  const activePath = pendingPath ?? location.pathname;

  const navigateTo = useCallback((path: string) => {
    if (path === location.pathname) {
      setPendingPath(null);
      return;
    }

    setPendingPath(path);
    void preloadRouteModule(path);
    if (navigationFrameRef.current !== null) cancelAnimationFrame(navigationFrameRef.current);
    navigationFrameRef.current = requestAnimationFrame(() => {
      navigationFrameRef.current = requestAnimationFrame(() => {
        navigationFrameRef.current = null;
        navigate(path);
      });
    });
  }, [location.pathname, navigate]);

  useEffect(() => () => {
    if (navigationFrameRef.current !== null) cancelAnimationFrame(navigationFrameRef.current);
  }, []);

  useEffect(() => {
    if (navigationFrameRef.current !== null) {
      cancelAnimationFrame(navigationFrameRef.current);
      navigationFrameRef.current = null;
    }
    setPendingPath(null);
    if (closingPathRef.current === location.pathname) return;
    closingPathRef.current = null;
    openTab(location.pathname);
  }, [location.pathname, openTab]);

  useEffect(() => {
    const active = scrollRef.current?.querySelector<HTMLElement>('[aria-selected="true"]');
    active?.scrollIntoView({ inline: 'nearest', block: 'nearest', behavior: 'auto' });
  }, [activePath, openPaths]);

  const closeAndNavigate = (path: string) => {
    const index = openPaths.indexOf(path);
    const isActive = location.pathname === path;
    if (isActive) closingPathRef.current = path;
    closeTab(path);
    if (!isActive) return;
    const remaining = openPaths.filter((item) => item !== path);
    navigateTo(remaining[Math.max(0, index - 1)] ?? HOME_PATH);
  };

  const reloadPath = (path: string) => {
    for (const queryKey of queryKeysByPath[path] ?? []) {
      void queryClient.invalidateQueries({ queryKey });
    }
    refreshTab(path);
    if (path !== location.pathname) navigateTo(path);
  };

  const closeSideAndNavigate = (path: string, side: 'left' | 'right') => {
    const index = openPaths.indexOf(path);
    const closed = side === 'left' ? openPaths.slice(1, index) : openPaths.slice(index + 1);
    const closesActivePath = closed.includes(location.pathname);
    if (closesActivePath) closingPathRef.current = location.pathname;
    if (side === 'left') closeLeft(path);
    else closeRight(path);
    if (closesActivePath) navigateTo(path);
  };

  const closeOthersAndNavigate = (path: string) => {
    const closesActivePath = location.pathname !== HOME_PATH && location.pathname !== path;
    if (closesActivePath) closingPathRef.current = location.pathname;
    closeOthers(path);
    if (closesActivePath) navigateTo(path);
  };

  const closeAllAndNavigate = () => {
    if (location.pathname !== HOME_PATH) closingPathRef.current = location.pathname;
    closeAll();
    navigateTo(HOME_PATH);
  };

  useEffect(() => {
    if (!contextMenu) return;
    const closeContextMenu = () => setContextMenu(null);
    const closeOnEscape = (event: KeyboardEvent) => { if (event.key === 'Escape') closeContextMenu(); };
    const preventBackgroundScroll = (event: Event) => event.preventDefault();
    window.addEventListener('pointerdown', closeContextMenu);
    window.addEventListener('blur', closeContextMenu);
    window.addEventListener('resize', closeContextMenu);
    window.addEventListener('wheel', preventBackgroundScroll, { passive: false, capture: true });
    window.addEventListener('touchmove', preventBackgroundScroll, { passive: false, capture: true });
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      window.removeEventListener('pointerdown', closeContextMenu);
      window.removeEventListener('blur', closeContextMenu);
      window.removeEventListener('resize', closeContextMenu);
      window.removeEventListener('wheel', preventBackgroundScroll, true);
      window.removeEventListener('touchmove', preventBackgroundScroll, true);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [contextMenu]);

  const openContextMenu = (event: React.MouseEvent | React.PointerEvent, path: string) => {
    event.preventDefault();
    event.stopPropagation();
    const menuWidth = 172;
    const menuHeight = 216;
    setContextMenu({
      path,
      left: Math.max(8, Math.min(event.clientX, window.innerWidth - menuWidth - 8)),
      top: Math.max(8, Math.min(event.clientY, window.innerHeight - menuHeight - 8)),
    });
  };

  const contextItems = (path: string): MenuProps['items'] => {
    const tabIndex = openPaths.indexOf(path);
    const otherClosableCount = openPaths.filter((itemPath) => itemPath !== HOME_PATH && itemPath !== path).length;
    return [
      { key: 'reload', icon: <ReloadOutlined />, label: t('common.refresh'), onClick: () => reloadPath(path) },
      { type: 'divider' },
      { key: 'close', icon: <CloseOutlined />, label: t('navigation.closeCurrent'), disabled: path === HOME_PATH, onClick: () => closeAndNavigate(path) },
      { key: 'close-left', icon: <ArrowLeftOutlined />, label: t('navigation.closeLeft'), disabled: tabIndex <= 1, onClick: () => closeSideAndNavigate(path, 'left') },
      { key: 'close-right', icon: <ArrowRightOutlined />, label: t('navigation.closeRight'), disabled: tabIndex === openPaths.length - 1, onClick: () => closeSideAndNavigate(path, 'right') },
      { type: 'divider' },
      { key: 'others', icon: <CloseCircleOutlined />, label: t('navigation.closeOthers'), disabled: otherClosableCount === 0, onClick: () => closeOthersAndNavigate(path) },
      { key: 'all', icon: <CloseCircleOutlined />, label: t('navigation.closeAll'), disabled: openPaths.length <= 1, onClick: closeAllAndNavigate },
    ];
  };

  return (
    <div className="workspace-tabs-shell" onContextMenu={(event) => event.preventDefault()}>
      <div ref={scrollRef} className="workspace-tabs" role="tablist" aria-label={t('navigation.openPages')}>
        {openPaths.map((path) => {
          const item = getNavigationItem(path);
          if (!item) return null;
          const active = activePath === path;
          return (
            <div
              key={path}
              className={`workspace-tab ${active ? 'active' : ''}`}
              role="tab"
              aria-selected={active}
              onPointerEnter={() => { void preloadRouteModule(path); }}
              onFocusCapture={() => { void preloadRouteModule(path); }}
              onPointerDown={(event) => { if (event.button === 2) openContextMenu(event, path); }}
              onContextMenu={(event) => openContextMenu(event, path)}
            >
                <button type="button" className="workspace-tab-main" onClick={() => navigateTo(path)}>
                  {path === HOME_PATH && <span className="workspace-tab-icon"><HomeFilled /></span>}
                  <span>{t(item.titleKey)}</span>
                </button>
                {path !== HOME_PATH && (
                  <Tooltip title={t('navigation.closeCurrent')}>
                    <button type="button" className="workspace-tab-close" aria-label={`${t('navigation.closeCurrent')} ${t(item.titleKey)}`} onClick={() => closeAndNavigate(path)}>
                      <CloseOutlined />
                    </button>
                  </Tooltip>
                )}
            </div>
          );
        })}
      </div>
      <Tooltip title={t('navigation.closeAll')}>
        <Button
          type="text"
          className="workspace-tabs-close-all"
          aria-label={t('navigation.closeAll')}
          icon={<CloseCircleOutlined />}
          disabled={openPaths.length <= 1}
          onClick={closeAllAndNavigate}
        />
      </Tooltip>
      {contextMenu && createPortal(
        <div className="workspace-tab-context-dropdown workspace-tab-context-popup" style={{ left: contextMenu.left, top: contextMenu.top }} onPointerDown={(event) => event.stopPropagation()}>
          <Menu items={contextItems(contextMenu.path)} selectable={false} onClick={() => setContextMenu(null)} />
        </div>,
        document.body,
      )}
    </div>
  );
}
